import { useLayoutEffect } from "react";

// Module-scoped, reference-counted lock state shared by every instance of
// this hook. Multiple sheets/dialogs can be mounted at the same time (e.g.
// the Custom Feature name dialog opened from within the Add Feature sheet),
// and previously each instance independently captured/restored raw body
// inline styles — whichever instance unmounted LAST would "win" and decide
// the restored state, but capture happened at MOUNT time, so an instance
// that mounted while another lock was already active captured the ALREADY
// LOCKED styles as its own "previous" state. Depending on unmount order and
// timing (e.g. a reload triggered by saving from a sheet racing with the
// sheet closing), this could leave `document.body` permanently stuck in
// `position: fixed` with a stale negative `top` offset — a state in which
// the body no longer participates in normal document scroll at all, so no
// content below one viewport height is reachable until a full page reload.
// A single shared counter plus a single captured "pre-lock" snapshot makes
// lock/unlock correct regardless of how many sheets are open at once or the
// order they close in: only the transition 0 -> 1 locks, only the
// transition 1 -> 0 restores, and restoration always uses the ONE snapshot
// taken before the first lock.
let lockCount = 0;
let preLockSnapshot = null;

function lockBodyScroll() {
  if (lockCount === 0) {
    const scrollY = window.scrollY;
    const { body } = document;
    const { documentElement: html } = document;

    preLockSnapshot = {
      scrollY,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    html.style.overflow = "hidden";
  }

  lockCount += 1;
}

function unlockBodyScroll() {
  lockCount = Math.max(0, lockCount - 1);

  if (lockCount === 0 && preLockSnapshot) {
    const { body } = document;
    const { documentElement: html } = document;
    const snapshot = preLockSnapshot;
    preLockSnapshot = null;

    body.style.overflow = snapshot.bodyOverflow;
    body.style.position = snapshot.bodyPosition;
    body.style.top = snapshot.bodyTop;
    body.style.width = snapshot.bodyWidth;
    html.style.overflow = snapshot.htmlOverflow;
    window.scrollTo(0, snapshot.scrollY);
  }
}

/**
 * Locks page scroll while active and restores the exact scroll position on
 * release. Safe to use from multiple simultaneously-mounted sheets/dialogs —
 * see the reference-counting rationale above.
 */
export function useBodyScrollLock(active = true) {
  useLayoutEffect(() => {
    if (!active || typeof document === "undefined") return undefined;

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [active]);
}
